import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import { AI_ASSISTANT_BLUE, MAX_CONVERSATION_MESSAGES } from "./constants";

function formatWhen(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const days = Math.floor((now - date) / 86400000);
  if (days < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

const ARTIFACT_LABEL = { presentation: "Presentation", quiz: "Quiz", question_set: "Questions" };

function HistoryRow({ item, active, onOpen, onMenu }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        px: 1.25,
        py: 1,
        borderRadius: 2,
        cursor: "pointer",
        border: "1px solid",
        borderColor: active ? "rgba(0,97,254,0.45)" : "transparent",
        bgcolor: active ? "rgba(0,97,254,0.06)" : "transparent",
        "&:hover": { bgcolor: active ? "rgba(0,97,254,0.08)" : "#f1f5f9" },
        "&:focus-visible": { outline: `2px solid ${AI_ASSISTANT_BLUE}` },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography noWrap fontWeight={700} sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "ink.strong" }}>
            {item.title || "New conversation"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>
            {formatWhen(item.last_activity_at)}
          </Typography>
        </Stack>
        {item.preview ? (
          <Typography
            sx={{
              fontSize: 12,
              color: "#64748b",
              mt: 0.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.preview}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={0.6} sx={{ mt: 0.6 }} flexWrap="wrap" useFlexGap>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: item.limit_reached ? "#b45309" : "#64748b",
            }}
          >
            {item.message_count}/{MAX_CONVERSATION_MESSAGES} messages
          </Typography>
          {item.has_draft ? (
            <Box
              component="span"
              sx={{ fontSize: 10.5, fontWeight: 700, px: 0.7, borderRadius: 999, color: "#1d4ed8", bgcolor: "rgba(0,97,254,0.1)" }}
            >
              <EditNoteRoundedIcon sx={{ fontSize: 12, verticalAlign: "-2px", mr: 0.2 }} />
              Draft
            </Box>
          ) : null}
          {(item.artifact_kinds || []).map((kind) => (
            <Box
              key={kind}
              component="span"
              sx={{ fontSize: 10.5, fontWeight: 700, px: 0.7, borderRadius: 999, color: "#065f46", bgcolor: "rgba(16,185,129,0.14)" }}
            >
              {ARTIFACT_LABEL[kind] || kind}
            </Box>
          ))}
        </Stack>
      </Box>
      <IconButton
        size="small"
        aria-label={`Options for ${item.title}`}
        onClick={(e) => {
          e.stopPropagation();
          onMenu(e.currentTarget, item);
        }}
        sx={{ mt: -0.25, color: "#64748b" }}
      >
        <MoreVertRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

HistoryRow.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onMenu: PropTypes.func.isRequired,
};

export default function AIConversationHistory({
  history,
  activeId,
  onBack,
  onSearch,
  onOpen,
  onRename,
  onDelete,
  onNewConversation,
}) {
  const [query, setQuery] = useState(history.query || "");
  const [menu, setMenu] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const searchTimer = useRef(null);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const handleQuery = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(value.trim()), 300);
  };

  const submitRename = async () => {
    if (!renaming) return;
    setBusy(true);
    const problem = await onRename(renaming.id, renameText);
    setBusy(false);
    if (problem) {
      setActionError(problem);
      return;
    }
    setRenaming(null);
  };

  const submitDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    const problem = await onDelete(confirmDelete.id);
    setBusy(false);
    setConfirmDelete(null);
    if (problem) setActionError(problem);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "background.elevated" }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, bgcolor: "background.card", borderBottom: "1px solid rgba(148,163,184,0.2)" }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <IconButton size="small" aria-label="Back to chat" onClick={onBack}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <Typography fontWeight={700} sx={{ flex: 1, fontSize: 14, color: "ink.strong" }}>
            Conversation history
          </Typography>
          <Button
            size="small"
            startIcon={<AddCommentOutlinedIcon fontSize="small" />}
            onClick={() => onNewConversation()}
            sx={{ textTransform: "none", fontWeight: 700, color: `${AI_ASSISTANT_BLUE} !important` }}
          >
            New
          </Button>
        </Stack>
        <TextField
          size="small"
          fullWidth
          placeholder="Search conversations"
          value={query}
          onChange={(e) => handleQuery(e.target.value)}
          inputProps={{ "aria-label": "Search conversations" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
        {actionError ? (
          <Typography role="alert" sx={{ fontSize: 12, color: "#b91c1c", px: 1, pb: 1 }}>
            {actionError}
          </Typography>
        ) : null}
        {history.loading && !history.items.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
            <CircularProgress size={22} sx={{ color: AI_ASSISTANT_BLUE }} />
          </Box>
        ) : null}
        {history.error ? (
          <Typography sx={{ fontSize: 12.5, color: "#b91c1c", px: 1 }}>{history.error}</Typography>
        ) : null}
        {!history.loading && !history.error && !history.items.length ? (
          <Typography sx={{ fontSize: 12.5, color: "#64748b", textAlign: "center", py: 4, px: 2 }}>
            {query ? "No conversations match that search." : "Your conversations will appear here."}
          </Typography>
        ) : null}
        <Stack spacing={0.5}>
          {history.items.map((item) =>
            renaming?.id === item.id ? (
              <Stack key={item.id} direction="row" spacing={0.75} sx={{ px: 1, py: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={renameText}
                  disabled={busy}
                  inputProps={{ maxLength: 120, "aria-label": "Conversation title" }}
                  onChange={(e) => setRenameText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitRename();
                    if (e.key === "Escape") setRenaming(null);
                  }}
                />
                <Button size="small" disabled={busy || !renameText.trim()} onClick={submitRename} sx={{ textTransform: "none", fontWeight: 700 }}>
                  Save
                </Button>
              </Stack>
            ) : (
              <HistoryRow
                key={item.id}
                item={item}
                active={item.id === activeId}
                onOpen={(row) => onOpen(row.id)}
                onMenu={(anchor, row) => setMenu({ anchor, item: row })}
              />
            )
          )}
        </Stack>
      </Box>

      <Menu anchorEl={menu?.anchor || null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            onOpen(menu.item.id);
            setMenu(null);
          }}
        >
          Open
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionError("");
            setRenaming(menu.item);
            setRenameText(menu.item.title || "");
            setMenu(null);
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionError("");
            setConfirmDelete(menu.item);
            setMenu(null);
          }}
          sx={{ color: "#b91c1c" }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(confirmDelete)} onClose={() => !busy && setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmDelete?.title}” and its unapproved drafts will be removed. Presentations, quizzes,
            questions and documents you already created from it are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={busy} onClick={() => setConfirmDelete(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={submitDelete}
            variant="contained"
            disableElevation
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#b91c1c !important", color: "#fff !important" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

AIConversationHistory.propTypes = {
  history: PropTypes.shape({
    loading: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.object),
    query: PropTypes.string,
    error: PropTypes.string,
  }).isRequired,
  activeId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onNewConversation: PropTypes.func.isRequired,
};
